// App shell — BrowserRouter + global header + routes.
//
// Decision history:
//   §4 (19E):    react-router-dom routes.
//   §G8 (19AS):  PROD-unlock for operator dashboard.
//   §S2 (19AT):  AppErrorBoundary + ToastProvider + per-route boundary.
//   §S4 (19AT):  lazy() split for non-hot views; Suspense fallback.

import { useEffect, useState, lazy, Suspense, type ReactNode } from 'react';
import { useNavigate, BrowserRouter, Routes, Route, Outlet, Link, useLocation } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { recordEvent } from './storage/idb.js';
import { HealthBadge } from './components/health-badge.js';
import { SyncStatusChip } from './components/sync-status-chip.js';
import { LanguageToggle, useT, useLocale } from './i18n/index.js';
import { HomePage } from './views/home.js';
import { CourseView } from './views/course.js';
import { CourseDashboard } from './views/course-dashboard.js';
import { FlashcardsView } from './views/flashcards-view.js';
import { QuizView } from './views/quiz-view.js';
import { DrillView } from './views/drill-view.js';
import { putDraft, putPdf } from './storage/idb.js';
import { getOrCreateAnonId } from './auth/anon-id.js';
import { ToastProvider, useToast } from './components/toast.js';
import { FeedbackPanelHost } from './components/feedback-panel.js';
import { AppErrorBoundary, RouteErrorBoundary } from './components/error-boundary.js';
import { PageLoadingFallback } from './components/loading-skeleton.js';
import { IdbHealthWatcher } from './components/idb-health-watcher.js';
import { EventBusProvider, eventBusInstance } from './lib/hooks/use-event-bus.js';
import { useThemeSync } from './lib/hooks/use-theme-sync.js';
import { useAuthInit } from './lib/hooks/use-auth-init.js';
import { useSaveKeyboard } from './lib/hooks/use-save-keyboard.js';
import { useSyncEngine } from './lib/hooks/use-sync-engine.js';
import { useProgressSyncGate } from './lib/hooks/use-progress-sync-gate.js';
import { useNotify } from './lib/hooks/use-notify.js';
import { MessageCenter } from './components/message-center.js';
import { ThemeToggle } from './components/theme-toggle.js';
import { UserAvatar } from './components/user-avatar.js';
import { useStore } from './lib/store/index.js';
import { useNotificationToast } from './lib/hooks/use-notification-toast.js';
import { SOCIAL_LINKS } from './config/social-links.js';
// create-course.css merged into styles/ layered structure (19BM-B Task 32).

// 19AT · S4: lazy-load non-hot-path views. Eager kept: HomePage, CourseView
// (layout shell), CourseDashboard, FlashcardsView, QuizView (most-used).
// Heavy / secondary views split out → smaller initial bundle.
const CreateCourseForm = lazy(() => import('./create-course/index.js').then((m) => ({ default: m.CreateCourseForm })));
const WrongAnswersView = lazy(() => import('./views/wrong-answers-view.js').then((m) => ({ default: m.WrongAnswersView })));
const BookmarksView = lazy(() => import('./views/bookmarks-view.js').then((m) => ({ default: m.BookmarksView })));
const PreviewView = lazy(() => import('./views/preview-view.js').then((m) => ({ default: m.PreviewView })));
const MockExamView = lazy(() => import('./views/mock-exam-view.js').then((m) => ({ default: m.MockExamView })));

const MindmapView = lazy(() => import('./views/mindmap-view.js').then((m) => ({ default: m.MindmapView })));
const GlossaryView = lazy(() => import('./views/glossary-view.js').then((m) => ({ default: m.GlossaryView })));
const LectureNotesView = lazy(() => import('./views/lecture-notes-view.js').then((m) => ({ default: m.LectureNotesView })));
const ProofDrillsView = lazy(() => import('./views/proof-drills-view.js').then((m) => ({ default: m.ProofDrillsView })));
const FreeResponseView = lazy(() => import('./views/free-response-view.js').then((m) => ({ default: m.FreeResponseView })));
const ReadingPassageView = lazy(() => import('./views/reading-passage-view.js').then((m) => ({ default: m.ReadingPassageView })));
const EssayView = lazy(() => import('./views/essay-view.js').then((m) => ({ default: m.EssayView })));
const CourseSettingsView = lazy(() => import('./views/course-settings-view.js').then((m) => ({ default: m.CourseSettingsView })));
const OperatorDashboard = lazy(() => import('./views/operator-dashboard.js').then((m) => ({ default: m.OperatorDashboard })));
const UsagePage = lazy(() => import('./admin/usage-page.js').then((m) => ({ default: m.UsagePage })));
const EvalPage = lazy(() => import('./admin/eval-page.js').then((m) => ({ default: m.EvalPage })));
const GuideView = lazy(() => import('./views/guide-view.js').then((m) => ({ default: m.GuideView })));
const OutlineView = lazy(() => import('./views/outline-view.js').then((m) => ({ default: m.OutlineView })));
const RegenView = lazy(() => import('./views/regen-view.js').then((m) => ({ default: m.RegenView })));
const HistoryView = lazy(() => import('./views/history-view.js').then((m) => ({ default: m.HistoryView })));

function AppHeader({ onOpenMessages }: { onOpenMessages: () => void }) {
  const t = useT();
  const { locale } = useLocale();
  const brandName = t('app.name');
  const brandMark = locale === 'zh' ? '考' : 'C';
  const unreadCount = useStore((s) => s.unreadCount);

  return (
    <header className="app-header">
      <Link to="/" className="brand brand-link" aria-label={brandName}>
        <span className="brand-mark" aria-hidden="true">{brandMark}</span>
        <span className="brand-text">{brandName}</span>
      </Link>
      <div className="header-tools-left">
        <UserAvatar />
        <button
          type="button"
          className={`header-msg-btn${unreadCount > 0 ? ' has-unread' : ''}`}
          onClick={onOpenMessages}
          aria-label={t('notif.messageCenter')}
        >
          <Bell size={18} aria-hidden="true" />
          {unreadCount > 0 && <span className="notif-dot" aria-hidden="true" />}
        </button>
      </div>
      <div className="header-tools-spacer" />
      <div className="header-tools-right">
        <ThemeToggle variant="header" />
        <LanguageToggle />
        <SyncStatusChip variant="header" />
        <HealthBadge />
      </div>
    </header>
  );
}

function MainLayout() {
  const t = useT();
  const { locale } = useLocale();
  const [msgDrawerOpen, setMsgDrawerOpen] = useState(false);
  const brandName = t('app.name');
  const brandMark = locale === 'zh' ? '考' : 'C';

  return (
    <div className="app">
      <AppHeader onOpenMessages={() => setMsgDrawerOpen(true)} />
      <Outlet />
      <MessageCenter open={msgDrawerOpen} onClose={() => setMsgDrawerOpen(false)} />
      <footer className="app-footer">
        <div className="app-footer-inner">
          {/* Left: logo + brand + version — aligns with header brand */}
          <div className="app-footer-left">
            <span className="app-footer-mark" aria-hidden="true">{brandMark}</span>
            <span className="app-footer-brand">{brandName}</span>
            <span className="app-footer-sep">·</span>
            <span className="app-footer-version">v{__APP_VERSION__}</span>
          </div>
          {/* Center: copyright */}
          <div className="app-footer-center">
            © {new Date().getFullYear()} ExamForge · {t('footer.allRightsReserved')}
          </div>
          {/* Right: social links — aligns with header tools */}
          <nav className="app-footer-right" aria-label="Social links">
            {SOCIAL_LINKS.map((link) => {
              const Icon = link.icon;
              return (
                <a
                  key={link.key}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="app-footer-link"
                  aria-label={t(link.labelKey)}
                  title={t(link.labelKey)}
                  style={link.brandHover ? ({ '--brand-hover': link.brandHover } as React.CSSProperties) : undefined}
                >
                  <Icon size={16} aria-hidden="true" />
                </a>
              );
            })}
          </nav>
        </div>
      </footer>
    </div>
  );
}

function CreatePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT();
  const anonId = getOrCreateAnonId();
  const notify = useNotify();

  return (
    <CreateCourseForm
      anonId={anonId}
      onCancel={() => navigate('/')}
      onSubmit={async (draft, files) => {
        try {
          for (const slot of draft.sources ?? []) {
            const file = files.get(slot.id);
            if (file) {
              await putPdf(slot.sha256, file);
            }
          }
          await putDraft(draft);
          // 19BN Phase D D6: notify the message center on course creation.
          notify({
            source: 'system',
            level: 'info',
            title: '课程已创建',
            message: draft.title,
            persistent: false,
          });
          navigate(`/course/${draft.id}?autoExtract=1`);
        } catch (e) {
          // 19AT · S2: replace alert() with toast. Storage failure (private
          // mode / quota) shows sticky error so user can copy + retry.
          // 19BM-C: console.error guarded — user-facing signal is the toast;
          // dev log kept for debugging, silenced in prod per CLAUDE.md.
          // eslint-disable-next-line no-console -- DEV-only diagnostic, stripped by Vite in prod build
          if (import.meta.env.DEV) console.error('Failed to persist draft:', e);
          toast.error(t('create.saveDraftFailed', { message: (e as Error).message ?? t('common.unknownError') }), { sticky: true });
        }
      }}
    />
  );
}

/**
 * 19AS · G9: install window-level error listeners once at App root.
 * Idempotent (StrictMode-safe) via module-level installed flag.
 */
let _errorCaptureInstalled = false;
function useGlobalErrorCapture(): void {
  useEffect(() => {
    if (_errorCaptureInstalled) return;
    _errorCaptureInstalled = true;
    if (typeof window === 'undefined') return;
    window.addEventListener('error', (ev: ErrorEvent) => {
      void recordEvent({
        type: 'runtime-error',
        summary: ev.message?.slice(0, 200) ?? 'window.onerror',
        viewPath: window.location.pathname,
        payload: {
          filename: ev.filename,
          lineno: ev.lineno,
          colno: ev.colno,
          stack: ev.error instanceof Error ? ev.error.stack?.slice(0, 4000) : undefined,
        },
      });
    });
    window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
      const reason = ev.reason;
      const summary = reason instanceof Error
        ? reason.message
        : typeof reason === 'string' ? reason : 'unhandled rejection';
      void recordEvent({
        type: 'unhandled-rejection',
        summary: summary.slice(0, 200),
        viewPath: window.location.pathname,
        payload: {
          stack: reason instanceof Error ? reason.stack?.slice(0, 4000) : undefined,
          reasonString: typeof reason === 'string' ? reason : undefined,
        },
      });
    });
  }, []);
}

/** 19AS · G8: PROD-unlock check for the operator dashboard.
 *  - Always visible in DEV.
 *  - In PROD: visible if `localStorage.examforge.operator === '1'` OR the URL
 *    carries `?op=1` (which we then persist so subsequent loads work). */
function OperatorLockedHint() {
  const t = useT();
  // E-5 (19BM · spec §3.2): inline style 收编为 class + token 化
  // (#5d6b80 → --color-ink-soft, #f1f3f7 → --color-surface-2).
  return (
    <div className="operator-locked-hint">
      <h1 className="operator-locked-title">{t('operator.locked.title')}</h1>
      <p className="operator-locked-body">
        {t('operator.locked.body1')}
      </p>
      <pre className="operator-locked-url">
        {typeof window !== 'undefined' ? `${window.location.origin}/__operator?op=1` : '/__operator?op=1'}
      </pre>
      <p className="operator-locked-body operator-locked-body2">
        {t('operator.locked.body2Prefix')}<strong>{t('operator.locked.body2Strong')}</strong>{t('operator.locked.body2Suffix')}
      </p>
    </div>
  );
}

function operatorVisible(): boolean {
  if (import.meta.env.DEV) return true;
  if (typeof window === 'undefined') return false;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get('op') === '1') {
      localStorage.setItem('examforge.operator', '1');
      return true;
    }
    return localStorage.getItem('examforge.operator') === '1';
  } catch {
    return false;
  }
}

/** 19AT · S2: helper — wrap a route element with RouteErrorBoundary +
 *  Suspense (for lazy-loaded children). resetKey = pathname so a navigation
 *  away clears any sticky error from the prior visit. */
function RouteShell({ scope, children }: { scope: string; children: ReactNode }) {
  const { pathname } = useLocation();
  return (
    <RouteErrorBoundary scope={scope} resetKey={pathname}>
      <Suspense fallback={<PageLoadingFallback />}>
        {children}
      </Suspense>
    </RouteErrorBoundary>
  );
}

function NotificationToastBridge() {
  useNotificationToast();
  return null;
}

export function App() {
  useGlobalErrorCapture();
  useThemeSync();
  useAuthInit();
  useSaveKeyboard();
  useSyncEngine();
  useProgressSyncGate();
  return (
    <AppErrorBoundary>
      <ToastProvider>
        <IdbHealthWatcher />
        <NotificationToastBridge />
        <EventBusProvider value={eventBusInstance}>
        <BrowserRouter>
          <Routes>
            <Route element={<MainLayout />}>
              <Route path="/" element={<RouteShell scope="home"><HomePage /></RouteShell>} />
              <Route path="/history" element={<RouteShell scope="history"><HistoryView /></RouteShell>} />
            </Route>
            <Route path="/create" element={<RouteShell scope="create"><CreatePage /></RouteShell>} />
            <Route path="/guide" element={<RouteShell scope="guide"><GuideView /></RouteShell>} />
            <Route path="/admin" element={<RouteShell scope="admin"><UsagePage /></RouteShell>} />
            <Route path="/admin/eval" element={<RouteShell scope="admin-eval"><EvalPage /></RouteShell>} />
            {/* 19BC-PROD: always-mounted route. When the visibility flag is
             *  unset (PROD without ?op=1), render a friendly hint instead
             *  of nothing — previously the route didn't mount at all and
             *  users saw a blank screen wondering what was wrong. */}
            <Route
              path="/__operator"
              element={
                <RouteShell scope="operator">
                  {operatorVisible() ? <OperatorDashboard /> : <OperatorLockedHint />}
                </RouteShell>
              }
            />
            <Route path="/course/:id" element={<RouteShell scope="course-shell"><CourseView /></RouteShell>}>
              <Route index element={<RouteShell scope="dashboard"><CourseDashboard /></RouteShell>} />
              <Route path="outline" element={<RouteShell scope="outline"><OutlineView /></RouteShell>} />
              <Route path="regen" element={<RouteShell scope="regen"><RegenView /></RouteShell>} />
              <Route path="flashcards" element={<RouteShell scope="flashcards"><FlashcardsView /></RouteShell>} />
              {/* 19AW: unified drill landing — sidebar's single drill entry. */}
              <Route path="drill" element={<RouteShell scope="drill"><DrillView /></RouteShell>} />
              <Route path="quiz" element={<RouteShell scope="quiz"><QuizView /></RouteShell>} />
              <Route path="mock" element={<RouteShell scope="mock-exam"><MockExamView /></RouteShell>} />

              <Route path="mindmap" element={<RouteShell scope="mindmap"><MindmapView /></RouteShell>} />
              <Route path="glossary" element={<RouteShell scope="glossary"><GlossaryView /></RouteShell>} />
              <Route path="lecture-notes" element={<RouteShell scope="lecture-notes"><LectureNotesView /></RouteShell>} />
              <Route path="proof-drills" element={<RouteShell scope="proof-drills"><ProofDrillsView /></RouteShell>} />
              <Route path="free-response" element={<RouteShell scope="free-response"><FreeResponseView /></RouteShell>} />
              <Route path="reading" element={<RouteShell scope="reading"><ReadingPassageView /></RouteShell>} />
              <Route path="essay" element={<RouteShell scope="essay"><EssayView /></RouteShell>} />
              <Route path="essay/:essayId" element={<RouteShell scope="essay-detail"><EssayView /></RouteShell>} />
              <Route path="bookmarks" element={<RouteShell scope="bookmarks"><BookmarksView /></RouteShell>} />
              <Route path="wrong-answers" element={<RouteShell scope="wrong-answers"><WrongAnswersView /></RouteShell>} />
              <Route path="settings" element={<RouteShell scope="settings"><CourseSettingsView /></RouteShell>} />
              {import.meta.env.DEV && (
                <Route path="preview" element={<RouteShell scope="preview"><PreviewView /></RouteShell>} />
              )}
            </Route>
          </Routes>
          {/* 19BM-F: global subjective-feedback hotkey (Cmd+/ · Ctrl+/).
              Mounted inside BrowserRouter (needs useLocation) + ToastProvider
              (needs useToast) + EventBusProvider. Works on every route. */}
          <FeedbackPanelHost />
        </BrowserRouter>
        </EventBusProvider>
      </ToastProvider>
    </AppErrorBoundary>
  );
}
